import Step from 'components/global/Step'
import { useEffect, useState } from 'react'
import RenewInfoBox from './RenewInfoBox'
import VerifyCardNumber from './VerifyCardNumber'
import RenewStatusBox from './RenewStatusBox'
import {
    GetOrderCardRenewTokenPresenter,
    useCreateOrderCardRenewSessionMutation,
} from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { UNLIMITED_RENEW_STEPS } from 'routes/steps'
import { setIsRenewProcessVerified } from 'store/unlimited-slice'
import { useCart } from 'store/cart-slice/hooks'
import { pushDataLayer } from 'helpers/analytics'

const RenewVerification = ({
    orderCardRenewTokenId,
    used: isTokenUsed,
    expired: isTokenExpired,
    nextEditionType,
}: GetOrderCardRenewTokenPresenter) => {
    const { language, navigateToStep } = useApp()
    const { refetchCart, isCartFetching } = useCart()
    const dispatch = useAppDispatch()
    const { setErrorMessages, getError, clearErrorMessages } =
        useErrorMessages()

    const { isRenewProcessVerified } = useAppSelector(
        (state) => state.unlimited
    )

    const [verifyCard, verifyCardStatus] =
        useCreateOrderCardRenewSessionMutation()

    const [cardNumber, setCardNumber] = useState('')

    const isCardNumberValid = /^\d+$/.test(cardNumber)
    const isVerificationReady = !isTokenUsed && !isTokenExpired

    const handleCardNumberChange = (number: string) => {
        if (getError('cardNumber')) clearErrorMessages()
        setCardNumber(number)
    }

    const handleVerifyCard = async () => {
        try {
            await verifyCard({
                createOrderCardRenewSessionCommand: {
                    token: orderCardRenewTokenId,
                    password: cardNumber,
                },
                _locale: language,
            }).unwrap()
            refetchCart(true)
            dispatch(setIsRenewProcessVerified(true))
        } catch (error) {
            setErrorMessages({
                messages: [
                    {
                        property_path: 'cardNumber',
                        message: 'Err',
                    },
                ],
            })
        }
    }

    useEffect(() => {
        if (!isCartFetching && isRenewProcessVerified)
            navigateToStep({ stepId: UNLIMITED_RENEW_STEPS[1].id })
    }, [isCartFetching, isRenewProcessVerified])

    useEffect(() => {
        // < analytics
        const redirectSource = 'direct'

        pushDataLayer({
            event: 'funnelStep',
            event_category: 'unlimited_renew',
            event_action: 'unlimited_order_start',
            event_label: redirectSource,
        })
        // analytics >
    }, [])

    return (
        <>
            <Step
                title={''}
                isButtonDisabled={!isCardNumberValid}
                isButtonLoading={verifyCardStatus.isLoading}
                isButtonVisible={isVerificationReady}
                onButtonClick={handleVerifyCard}
            >
                {isVerificationReady && !!orderCardRenewTokenId && (
                    <>
                        <RenewInfoBox cardEdition={nextEditionType} />
                        <VerifyCardNumber
                            handleCardNumberChange={handleCardNumberChange}
                        />
                    </>
                )}
                {!isVerificationReady && (
                    <RenewStatusBox status={isTokenUsed ? 'used' : 'expired'} />
                )}
            </Step>
        </>
    )
}

export default RenewVerification
